import { ref, watch, onMounted } from 'vue';

// 安全地访问localStorage的辅助函数
const safeLocalStorage = {
  getItem(key) {
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        return localStorage.getItem(key);
      }
    } catch (e) {
      console.warn('Error accessing localStorage:', e);
    }
    return null;
  },
  setItem(key, value) {
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        localStorage.setItem(key, value);
      }
    } catch (e) {
      console.warn('Error writing to localStorage:', e);
    }
  }
};

/**
 * 提供深色/浅色模式切换功能的组合式函数
 * @returns {Object} - 包含深色模式状态和切换方法的对象
 */
export function useDarkMode() {
  // 创建一个响应式的暗黑模式状态，默认为true(暗黑模式)
  const isDarkMode = ref(true);
  
  // 检查是否有保存的颜色主题设置
  const loadSavedTheme = () => {
    const savedTheme = safeLocalStorage.getItem('color-theme');
    
    // 如果localStorage中有主题设置
    if (savedTheme === 'light') {
      // 只有明确设置为light时才使用浅色模式
      isDarkMode.value = false;
      document.documentElement.classList.remove('dark');
    } else if (savedTheme === 'dark' || 
        (!savedTheme && window.matchMedia('(prefers-color-scheme: dark)').matches) ||
        !savedTheme) {
      // 如果设置为dark，或者没有设置但系统偏好暗色，或者没有任何设置 - 都使用暗色模式
      isDarkMode.value = true;
      document.documentElement.classList.add('dark');
    }
  };
  
  // 切换颜色主题
  const toggleDarkMode = () => {
    isDarkMode.value = !isDarkMode.value;
    
    if (isDarkMode.value) {
      document.documentElement.classList.add('dark');
      safeLocalStorage.setItem('color-theme', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      safeLocalStorage.setItem('color-theme', 'light');
    }
  };
  
  // 监听系统主题变化
  onMounted(() => {
    try {
      // 在组件挂载时加载保存的主题
      loadSavedTheme();
      
      // 监听系统主题变更
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      
      // 旧版本和新版本浏览器兼容性处理
      if (mediaQuery.addEventListener) {
        mediaQuery.addEventListener('change', e => {
          if (!safeLocalStorage.getItem('color-theme')) {
            if (e.matches) {
              isDarkMode.value = true;
              document.documentElement.classList.add('dark');
            } else {
              // 即使系统不是暗黑模式，也不切换，保持暗黑模式
              // 除非用户明确设置过主题
              const savedTheme = safeLocalStorage.getItem('color-theme');
              if (savedTheme === 'light') {
                isDarkMode.value = false;
                document.documentElement.classList.remove('dark');
              }
            }
          }
        });
      } else {
        mediaQuery.addListener(e => {
          if (!safeLocalStorage.getItem('color-theme')) {
            if (e.matches) {
              isDarkMode.value = true;
              document.documentElement.classList.add('dark');
            } else {
              // 即使系统不是暗黑模式，也不切换，保持暗黑模式
              // 除非用户明确设置过主题
              const savedTheme = safeLocalStorage.getItem('color-theme');
              if (savedTheme === 'light') {
                isDarkMode.value = false;
                document.documentElement.classList.remove('dark');
              }
            }
          }
        });
      }
    } catch (e) {
      console.error('Error setting up dark mode:', e);
      // 默认使用暗黑模式作为回退方案
      isDarkMode.value = true;
      document.documentElement.classList.add('dark');
    }
  });
  
  return {
    isDarkMode,
    toggleDarkMode
  };
}

export default useDarkMode; 